import { useCallback, useMemo } from "react"
import { useContextSelector as useCS } from "use-context-selector"
import { ErrorScreen } from "@unlimint/admin-ui-kit"

import { useTranslate } from "infrastructure/hooks/useTranslate"

import { FormModeEnum } from "domain/enums/FormModeEnum"
import { FormContainer } from "domain/core/containers/FormContainer"
import { updateToGridDataState } from "domain/core/utils"
import { GlobalConfigContext } from "domain/globalConfig/GlobalConfigContext"
import { globalConfigSelectors } from "domain/globalConfig/selectors"

import { EditWebhookForm } from "view/pages/devSpace/WebhooksPage/components/EditWebhookForm"

import { validate } from "./validate"
import { WebhookContext } from "../WebhookContext"
import { webhookSelectors } from "../selectors"
import { dataToRow, mapToReqModel } from "../mappers"
import { webhookSecret } from "../services/WebhookService"
import { formatEventLabelType } from "../utils"

export const EditWebhookContainer = ({ modal, formData }) => {
  const editTitle = useTranslate("pages.webhooks.modals.editTitle")
  const editButton = useTranslate("pages.webhooks.modals.editButton")

  const gridData = useCS(WebhookContext, webhookSelectors.gridData)
  const update = useCS(WebhookContext, webhookSelectors.update)
  const webhookEvents = useCS(
    GlobalConfigContext,
    globalConfigSelectors.webhookEvents
  )

  const disabled = !webhookEvents
  const eventOptions = useMemo(() => {
    if (!webhookEvents?.data) return null
    return Object.keys(webhookEvents.data).map((key) => ({
      label: formatEventLabelType(key),
      value: key,
    }))
  }, [webhookEvents])

  const handlerRefreshSecret = async (id) => {
    const { data, error } = await webhookSecret.refreshSecret(id)

    update({
      gridData: updateToGridDataState(gridData, dataToRow(data)),
    })

    return data
  }

  const cbSuccess = useCallback(
    (data) => {
      update({
        gridData: updateToGridDataState(gridData, dataToRow(data.webhook)),
      })
    },
    [gridData, update]
  )

  if (webhookEvents?.error)
    return (
      <ErrorScreen errorText="Unable to Edit this Webhook. Try again later." />
    )
  if (!eventOptions) return null

  return (
    <FormContainer
      mode={FormModeEnum.Edit}
      validate={validate}
      initialValues={formData}
      url={`/v1/webhooks/${formData?.id}`}
      Form={EditWebhookForm}
      mapToReqModel={mapToReqModel}
      formProps={{
        title: editTitle,
        actionButtonTitle: editButton,
        onClose: modal.onClose,
        refreshSecret: handlerRefreshSecret,
        disabled,
        eventOptions,
        webhookEvents,
      }}
      cbSuccess={cbSuccess}
    />
  )
}
